import React, { useState, useCallback } from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useCreateOpportunity } from "@/hooks/queries";
import { useNotification } from "@/hooks/useNotification";
import type { ColumnDef } from "./columnConfig";
import type { OpportunitySubcategory } from "@api/opportunity";
import { SubcategorySelector } from "./SubcategorySelector";
import styles from "./styles.module.css";

type Props = {
  categoryId: string;
  onCancel: () => void;
  columns: ColumnDef[];
  categoryName: string;
  defaultSubcategory: string | null;
  subcategories: OpportunitySubcategory[];
};

type FormValues = {
  primary_date: string | null;
  subcategory_id: string | null;
  fields: Record<string, string | null>;
};

const makeInitialValues = (columns: ColumnDef[], defaultSubcategory: string | null): FormValues => {
  const fields: Record<string, string | null> = {};
  for (const col of columns) {
    // Skip the primary-sort date (held separately) and read-only derived ints (e.g. future_stops).
    if (!col.isPrimarySort && !col.isInt) {
      fields[col.field] = col.isDate ? null : "";
    }
  }
  return {
    primary_date: null,
    subcategory_id: defaultSubcategory,
    fields,
  };
};

export const AddOpportunityRow = ({ categoryId, onCancel, columns, categoryName, defaultSubcategory, subcategories }: Props) => {
  const createOpportunityMutation = useCreateOpportunity();
  const { notifySuccess, notifyError } = useNotification();
  const [formValues, setFormValues] = useState<FormValues>(makeInitialValues(columns, defaultSubcategory));
  const hasSubcategories = subcategories.length > 0;

  const handleFieldChange = useCallback(
    (field: string, isPrimarySort: boolean) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isPrimarySort) {
        setFormValues((prev) => ({ ...prev, primary_date: e.target.value }));
      } else {
        setFormValues((prev) => ({
          ...prev,
          fields: { ...prev.fields, [field]: e.target.value },
        }));
      }
    },
    []
  );

  const handleDateChange = useCallback(
    (field: string, isPrimarySort: boolean) => (newVal: Dayjs | null) => {
      const value = newVal ? newVal.toISOString() : null;
      if (isPrimarySort) {
        setFormValues((prev) => ({ ...prev, primary_date: value }));
      } else {
        setFormValues((prev) => ({
          ...prev,
          fields: { ...prev.fields, [field]: value },
        }));
      }
    },
    []
  );

  const handleSave = useCallback(() => {
    createOpportunityMutation.mutate(
      {
        category_id: categoryId,
        primary_date: formValues.primary_date,
        subcategory_id: formValues.subcategory_id,
        fields: formValues.fields,
      },
      {
        onSuccess: () => {
          notifySuccess("Opportunity created");
          onCancel();
        },
        onError: () => notifyError("Failed to create opportunity"),
      }
    );
  }, [categoryId, formValues, onCancel, createOpportunityMutation, notifySuccess, notifyError]);

  const getEditValue = (col: ColumnDef): string | null => {
    if (col.isPrimarySort) return formValues.primary_date;
    return formValues.fields[col.field] ?? null;
  };

  return (
    <tr>
      {columns.map((col) => (
        <td key={col.field}>
          {col.isInt ? (
            <span>—</span>
          ) : col.isDate ? (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={getEditValue(col) ? dayjs(getEditValue(col)) : null}
                onChange={handleDateChange(col.field, !!col.isPrimarySort)}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "standard",
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          ) : (
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={getEditValue(col) ?? ""}
              onChange={handleFieldChange(col.field, !!col.isPrimarySort)}
            />
          )}
        </td>
      ))}
      <td className={styles.actionsCell}>
        {hasSubcategories && (
          <SubcategorySelector
            value={formValues.subcategory_id}
            onChange={(sub) => setFormValues((prev) => ({ ...prev, subcategory_id: sub }))}
            disabled={createOpportunityMutation.isPending}
            subcategories={subcategories}
          />
        )}
        <IconButton size="small" onClick={handleSave} color="primary" disabled={createOpportunityMutation.isPending || (hasSubcategories && !formValues.subcategory_id)}>
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onCancel} disabled={createOpportunityMutation.isPending}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </td>
    </tr>
  );
};
